import { useConversationJobsWorker } from '@/hooks/useConversationJobsWorker';

export interface UseConversationActiveJobsResult {
  /** True when the conversation has any queued or running job (JQ/JQA/JR/JRA). */
  hasActiveJobs: boolean;
  /** Raw jobs array from the poller, for the read-only active-scans list. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  jobs: any[];
  /** True while the first poll for this conversation is in flight. */
  isLoading: boolean;
}

/**
 * A conversation job is "active" when its backend status code is queued (JQ) or
 * running (JR). The async variants (JQA / JRA) share those prefixes, so a
 * prefix check covers all four states. Terminal states (JP pass, JF fail) and
 * the frontend-only Partial (JX) are not active.
 *
 * Mirrors the in-progress filter in `ConversationJobs.tsx` so the stop button
 * and the active-scans list agree on what "still running" means.
 */
export function isActiveJobStatus(status: unknown): boolean {
  if (typeof status !== 'string') return false;
  const code = status.split('#')[0];
  return code.startsWith('JQ') || code.startsWith('JR');
}

/**
 * Background capability jobs (status JQ/JR) spawned by the agent keep running
 * after the conversation planner goes idle. This hook surfaces whether such
 * jobs exist for the selected conversation, reusing the purpose-built
 * conversation jobs poller, so the stop button can stay available while work
 * is still in flight.
 *
 * Cancellation is NOT done here: the backend `POST /planner/stop` endpoint
 * already cancels every non-terminal job for the conversation server-side
 * (`cancelSubagentJobs` in backend/pkg/handler/handlers/conversation/stop.go),
 * even when the planner is idle. Cancelling from the client would duplicate
 * those `DELETE /job/` calls and spam a per-job "Cancelling job…" toast.
 *
 * Polling is gated on `conversationId`: `useConversationJobsWorker` only starts
 * its worker when an id is present, so this hook is inert for unselected
 * conversations.
 */
export function useConversationActiveJobs(
  conversationId?: string
): UseConversationActiveJobsResult {
  const {
    data: jobs,
    error,
    isLoading,
  } = useConversationJobsWorker(conversationId);

  // Fail-safe: the worker keeps its last `jobs` value when polling errors
  // (e.g. a Cognito token expiring after ~1h leaves the worker polling with a
  // stale token and getting 401s). Without this guard the stale `jobs` array
  // would pin `hasActiveJobs` true forever. On error we assume no active jobs
  // so the stop-button signal degrades gracefully to the planner-only signals.
  const hasActiveJobs =
    !error &&
    (Array.isArray(jobs) ? jobs : []).some((job: { status?: unknown }) =>
      isActiveJobStatus(job?.status)
    );

  // Gate `jobs` on the same `!error` condition as `hasActiveJobs`: the
  // ConversationJobs "Active Security Scans" list and the composer Stop button
  // must degrade together. Otherwise a mid-scan poll error (e.g. Cognito token
  // expiry) would hide the Stop button (hasActiveJobs=false) while the list kept
  // rendering the worker's stale `jobs` — stale scans shown with no stop affordance.
  return {
    hasActiveJobs,
    jobs: !error && Array.isArray(jobs) ? jobs : [],
    isLoading,
  };
}
